import { type CriteriaFilterValue, Divider } from "@stacklet/ui";
import { z } from "zod";

import {
  type FilterElementInput,
  type FilterValueInput,
} from "app/utils/filters";

export const RULES_PAGE_SIZE = 5;

const filterValueInputSchema: z.ZodType<FilterValueInput> = z.object({
  name: z.string(),
  operator: z.string().optional().nullable(),
  value: z.string().optional().nullable(),
});

const filterElementInputSchema: z.ZodType<FilterElementInput> = z.object({
  single: filterValueInputSchema.nullish(),
  multiple: z
    .object({
      operator: z.enum(["AND", "OR", "NOT"]),
      operands: z.array(z.lazy(() => filterElementInputSchema)),
    })
    .nullish(),
});

const criteriaFilterValueSchema: z.ZodType<CriteriaFilterValue> = z.object({
  criteria: z.string(),
  name: z.string(),
  operator: z.string().optional(),
  value: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    )
    .optional(),
});

export const criteriaFilterValueArraySchema = z.array(
  criteriaFilterValueSchema,
);

export const RULES_FILTERS_HELP_CONTENT = (
  <div className="flex flex-col gap-y-6">
    <div>
      <div className="mb-lg text-label-large-strong">Overview</div>
      <div>
        Create a notification rule to automatically create Jira issues and
        assign them to resource owners.
      </div>
    </div>
    <Divider className="my-sm opacity-50" orientation="horizontal" />
    <div>
      <div className="mb-lg text-label-large-strong">Resource Criteria</div>
      <div>
        Select a set set of filters to filter resources by account, resource
        tag, resource type, or account attributes to help narrow down the list
        of resources for notification recipients.
      </div>
    </div>
    <Divider className="my-sm opacity-50" orientation="horizontal" />
    <div>
      <div className="mb-xxs text-label-large-strong">Filters</div>
      <div>
        Select a set of filters based on policy metadata, including policy name,
        policy collection, or binding.
      </div>
    </div>

    <div>
      <b>Policy Metadata: </b> Use a jmespath to include resource matches based
      on policy metadata.
    </div>
    <div>
      <b>Category:</b> Select a resource match by policy category
    </div>
    <div>
      <b>Severity:</b> Select a resource match by policy severity
    </div>
  </div>
);

export const RULES_DELIVERY_METHOD_HELP_CONTENT = (
  <div className="flex flex-col gap-y-6">
    <div>
      <div className="mb-lg text-label-large-strong">Overview</div>
      <div>
        Configure how Stacklet creates notifications for new resource matches.
      </div>
    </div>
    <Divider className="my-sm opacity-50" orientation="horizontal" />
    <div>
      <div className="mb-xxs text-label-large-strong">Jira</div>
      <div>Use Stacklet to automatically create Jira issues.</div>
    </div>
    <div>
      Stacklet will first group resource matches by issue assignee. You can
      select auto assignee from one of the following options:
    </div>

    <ul className="list-inside list-disc pl-4">
      <li>Resource Owner</li>
      <li>Account Owner</li>
      <li>Account Variable</li>
      <li>Manual Value</li>
      <li>No Assignee</li>
    </ul>

    <div>
      <b>Resource Owner</b> and <b>Account Owner</b> are determined by your
      resource owner tag configuration.
    </div>
    <div>
      Account Variable uses account level variables to determine the issue
      assignee.
    </div>
    <div>Manual Value uses a user-supplied custom assignee value.</div>
    <div>
      No Assignee will create an issue without an assignee and will not group
      resources.
    </div>

    <div>
      Next, Stacklet will group resource matches based on the following groups:
    </div>
    <div>
      <ul className="list-inside list-disc pl-4">
        <li>Policy, Account, and Resource Tag</li>
        <li>Policy and Account</li>
        <li>Policy</li>
      </ul>
    </div>

    <div>
      Policy, Account, and Resource Tag will result in the fewest issues
      created. Resource matches will be grouped together by the Policy, Account,
      and a user supplied Resource Tag. For resources missing the tag, they will
      be grouped in an issue for each Policy and Account.
    </div>
  </div>
);
